/** Asset loading scene — shows a progress bar while preloading all game assets, then transitions to GameScene. */
import Phaser from 'phaser';
import { fadeIn, fadeToScene } from '../utils/SceneTransitions';
import assetRegistry from '../data/assets.json';
import { crispText, crispRect, CRISP_SCALE, setupCrispCamera } from '../ui/CrispUtil';
import { Font, Hex, Color } from '../ui/Theme';
import { GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';

interface AssetEntry {
  key: string;
  path: string;
}

interface SpritesheetEntry extends AssetEntry {
  frameWidth: number;
  frameHeight: number;
}

interface AtlasEntry extends AssetEntry {
  jsonPath: string;
}

interface AssetRegistry {
  images: AssetEntry[];
  atlases?: AtlasEntry[];
  tilemaps: AssetEntry[];
  audio: AssetEntry[];
  spritesheets: SpritesheetEntry[];
}

interface BootData {
  saveName: string;
  isNew: boolean;
}

/**
 * Loads all images, tilemaps, audio, and spritesheets from assets.json, then applies
 * nearest-neighbor filtering to every loaded texture before handing off to GameScene.
 */
export class BootScene extends Phaser.Scene {
  private saveData?: BootData;
  private spritesheetKeys = new Set<string>();

  constructor() {
    super({ key: 'BootScene' });
  }

  /** Capture save name and new-game flag before asset loading begins. */
  init(data: BootData): void {
    this.saveData = data;
  }

  /** Render the loading screen and kick off all asset loads with progress bar feedback. */
  preload(): void {
    setupCrispCamera(this);
    const width = GAME_WIDTH;
    const height = GAME_HEIGHT;

    crispText(this, width / 2, height / 2, 'MAGZIGEDDON', {
      fontFamily: Font.family, fontSize: Font.sizeTitleMd, color: Hex.greenBright,
    }).setOrigin(0.5);

    const progressText = crispText(this, width / 2, height / 2 + 48, 'Loading...', {
      fontFamily: Font.family, fontSize: '16px', color: Hex.textMuted,
    }).setOrigin(0.5);

    // Progress bar
    crispRect(this, width / 2, height / 2 + 80, 300, 16, Color.overlayBg);
    const barFill = crispRect(this, width / 2 - 150, height / 2 + 80, 0, 16, Color.greenBright);
    barFill.setOrigin(0, 0.5);

    this.load.on('progress', (value: number) => {
      progressText.setText(`Loading... ${Math.round(value * 100)}%`);
      barFill.width = 300 * value * CRISP_SCALE;
    });

    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.error(`Failed to load asset: ${file.key} (${file.url})`);
    });

    this.loadAssets(assetRegistry as AssetRegistry);

    fadeIn(this);
  }

  create(): void {
    // Spread texture filter uploads across frames to avoid a GPU spike
    // that triggers Chrome's adaptive vsync throttle (locks to 30fps).
    const keys: string[] = [];
    this.textures.each((texture) => {
      keys.push(texture.key);
    }, this);

    const BATCH_SIZE = 3;
    let index = 0;

    const processBatch = (): void => {
      const end = Math.min(index + BATCH_SIZE, keys.length);
      for (let i = index; i < end; i++) {
        const tex = this.textures.get(keys[i]);
        if (!tex) continue;
        // Spritesheets and high-res atlases downscale at runtime — LINEAR gives
        // clean bilinear filtering. Everything else gets NEAREST for crisp pixels.
        const linear = this.spritesheetKeys.has(keys[i]) || keys[i] === 'attunement-icons' || keys[i] === 'node-icons';
        const filter = linear
          ? Phaser.Textures.FilterMode.LINEAR
          : Phaser.Textures.FilterMode.NEAREST;
        tex.setFilter(filter);
      }
      index = end;

      if (index < keys.length) {
        requestAnimationFrame(processBatch);
      } else {
        fadeToScene(this, 'GameScene', this.saveData);
      }
    };

    processBatch();
  }

  private loadAssets(registry: AssetRegistry): void {
    for (const asset of registry.images) {
      this.load.image(asset.key, asset.path);
    }

    if (registry.atlases) {
      for (const atlas of registry.atlases) {
        this.load.atlas(atlas.key, atlas.path, atlas.jsonPath);
      }
    }

    for (const asset of registry.tilemaps) {
      this.load.tilemapTiledJSON(asset.key, asset.path);
    }

    for (const asset of registry.audio) {
      this.load.audio(asset.key, asset.path);
    }

    for (const asset of registry.spritesheets) {
      this.spritesheetKeys.add(asset.key);
      this.load.spritesheet(asset.key, asset.path, {
        frameWidth: asset.frameWidth,
        frameHeight: asset.frameHeight,
      });
    }
  }
}
